import React, { useEffect, useState } from 'react';
import './BannerComponent.css';
import { Link } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';

interface BannerProps {
  title?: string;
  message: string;
  link?: {
    text: string;
    url: string;
  };
  variant?: 'info' | 'warning' | 'success';
  dismissible?: boolean;
}

const BannerComponent: React.FC<BannerProps> = ({
  title,
  message,
  link,
  variant = 'info',
  dismissible = true,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    document.body.classList.toggle('v2-has-banner', isVisible);
    return () => document.body.classList.remove('v2-has-banner');
  }, [isVisible]);

  const handleLinkClick = () => {
    trackEvent('banner_click', {
      event_category: 'Banner',
      event_label: link?.text || 'Banner Link',
      value: 1,
    });
  };

  const handleDismiss = () => {
    trackEvent('banner_close', {
      event_category: 'Banner',
      event_label: title || 'Banner Closed',
      value: 1,
    });
    setIsVisible(false);
  };

  if (!isVisible) return null;

  const renderLink = () => {
    if (!link) return null;
    const inner = (
      <>
        {link.text}
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <path d="M3 8h10M9 4l4 4-4 4" />
        </svg>
      </>
    );
    return link.url.startsWith('/') ? (
      <Link to={link.url} className="v2-banner-cta" onClick={handleLinkClick}>
        {inner}
      </Link>
    ) : (
      <a
        href={link.url}
        className="v2-banner-cta"
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleLinkClick}
      >
        {inner}
      </a>
    );
  };

  return (
    <>
    <div className="v2-banner-spacer" />
    <div className={`v2-banner v2-banner-${variant}`} role="region" aria-label="Promotional banner">
      <div className="v2-banner-inner">
        <span className="v2-banner-dot" aria-hidden="true" />
        {title && <span className="v2-banner-title">{title}</span>}
        <span className="v2-banner-message">{message}</span>
        {renderLink()}
      </div>
      {dismissible && (
        <button
          className="v2-banner-close"
          onClick={handleDismiss}
          aria-label="Dismiss banner"
          type="button"
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      )}
    </div>
    </>
  );
};

export default BannerComponent;
